import React from 'react';
import { cn } from '@/utils/helpers';

// 卡片变体类型
export type CardVariant = 'default' | 'outlined' | 'elevated' | 'filled';

// 卡片尺寸类型
export type CardSize = 'sm' | 'md' | 'lg';

// 卡片属性接口
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  size?: CardSize;
  hoverable?: boolean;
  selected?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}

// 卡片头部属性接口
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}

// 卡片内容属性接口
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

// 卡片底部属性接口
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

// 变体样式映射 - Fluent Design 风格
const variantStyles: Record<CardVariant, string> = {
  default: 'card',
  outlined: 'acrylic-card border-2',
  elevated: 'card hover-lift',
  filled: 'acrylic-card'
};

// 尺寸样式映射 - Fluent Design 风格
const sizeStyles: Record<CardSize, string> = {
  sm: 'p-4',
  md: 'p-6', 
  lg: 'p-8'
};

// 主卡片组件
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      size = 'md',
      hoverable = false,
      selected = false,
      disabled = false,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          // 基础样式
          'transition-all duration-300 ease-out relative overflow-hidden',
          // 变体样式
          variantStyles[variant],
          // 尺寸样式
          sizeStyles[size],
          // 悬停效果
          hoverable && !disabled && 'hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] cursor-pointer transform',
          // 选中状态
          selected && 'ring-2 ring-blue-400 ring-offset-2 ring-offset-transparent shadow-2xl scale-[1.02]',
          // 禁用状态
          disabled && 'opacity-50 cursor-not-allowed',
          // 自定义类名
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// 卡片头部组件
export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn('flex items-start justify-between mb-4', className)}
      {...props}
    >
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="text-sm text-gray-600 mt-1">
            {subtitle}
          </p>
        )}
        {children}
      </div>
      {action && (
        <div className="flex-shrink-0 ml-4">
          {action}
        </div>
      )}
    </div>
  );
};

// 卡片内容组件
export const CardContent: React.FC<CardContentProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn('text-gray-700', className)}
      {...props}
    >
      {children}
    </div>
  );
};

// 卡片底部组件
export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn('flex items-center justify-between mt-4 pt-4 border-t border-gray-200', className)}
      {...props}
    >
      {children}
    </div>
  );
};

// 可选择卡片组件
export interface SelectableCardProps {
  value: string;
  selectedValue?: string;
  onSelect?: (value: string) => void;
  name?: string;
  variant?: CardVariant;
  size?: CardSize;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const SelectableCard: React.FC<SelectableCardProps> = ({
  value,
  selectedValue,
  onSelect,
  name,
  children,
  disabled,
  ...props
}) => {
  const isSelected = selectedValue === value;

  const handleClick = () => {
    if (!disabled && onSelect) {
      onSelect(value);
    }
  };

  return (
    <Card
      {...props}
      selected={isSelected}
      hoverable={!disabled}
      disabled={disabled}
      onClick={handleClick}
      role="radio"
      aria-checked={isSelected}
      aria-labelledby={name}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {children}
    </Card>
  );
};

// 加载状态卡片组件
export const LoadingCard: React.FC<{ size?: CardSize; className?: string }> = ({
  size = 'md',
  className
}) => {
  return (
    <Card size={size} className={className}>
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="h-20 bg-gray-200 rounded mb-4"></div>
        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
      </div>
    </Card>
  );
};

export default Card;